import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form, Field } from "formik";
import axios from "axios";
import { fetchCandidateWorkExperience } from "../../store/portfolio/action";
import {
  putCandidate,
  fetchCandidate,
} from "@workspace/candidate/src/store/candidate/action";
import { BASE_CANDIDATE_WORK_EXPERIENCE } from "@workspace/portfolio/src/helpers/url_helper";
import { CANDIDATE_WORK_EXPERIENCE_URL } from "@workspace/common/src/config";
import { CandidateEntityConstant } from "@workspace/candidate/src/constants/candidateConstant";
import { toast } from "react-toastify";
import "./CandidateProjectDetailsSection.scss";
import { CandidateProjectMedia } from "@workspace/portfolio/src/components";

export const CandidateProjectDetailsSection = ({ id, publicView }) => {
  const dispatch = useDispatch();

  const candidateWorkExperience = useSelector(
    (state) => state?.PortfolioReducer?.candidateWorkExperience
  );
  const candidate = useSelector((state) => state?.CandidateReducer?.candidate);

  const [editModeId, setEditModeId] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [candidateProjectData, setCandidateProjectData] = useState([]);
  const [publicProjectExperience, setPublicProjectExperience] = useState(null);

  // Fetch Candidate Work Experience
  useEffect(() => {
    if (id) {
      if (publicView) {
        axios
          .get(
            `${CANDIDATE_WORK_EXPERIENCE_URL}${BASE_CANDIDATE_WORK_EXPERIENCE}/public/entity/${CandidateEntityConstant.CANDIDATE_WORK_EXPERIENCE}/${id}`
          )
          .then((response) => {
            setPublicProjectExperience(response.data);
          });
      } else {
        dispatch(
          fetchCandidateWorkExperience({
            entity: CandidateEntityConstant.CANDIDATE_WORK_EXPERIENCE,
            id: id,
          })
        );
      }
    }
  }, [id, publicView]);

  // Parse Submission Data
  const parseWorkExperience = (workExperience) => {
    const parsedData = workExperience.map((item) => {
      const submissionData = JSON.parse(item.submissionData);
      for (const key in submissionData) {
        if (submissionData[key] === "") {
          submissionData[key] = null;
        }
      }
      return {
        ...item,
        submissionData,
      };
    });
    return parsedData;
  };

  // Parse Work Experience
  useEffect(() => {
    if (publicProjectExperience != null) {
      const parsedProject = parseWorkExperience(publicProjectExperience);
      setCandidateProjectData(parsedProject);
    } else if (candidateWorkExperience != null) {
      const parsedWorkExperience = parseWorkExperience(candidateWorkExperience);
      setCandidateProjectData(parsedWorkExperience);
    }
  }, [candidateWorkExperience, publicProjectExperience]);

  useEffect(() => {
    const isFormIdMatch = candidateWorkExperience.some(
      (item) => item.formId === candidate?.formId
    );

    if (isFormIdMatch) {
      let updatedCandidateWorkExperience;

      // Find if the formSubmissionId matches any in candidateWorkExperience
      const isFormSubmissionIdMatch = candidateWorkExperience.some(
        (item) => item.formSubmissionId === candidate?.formSubmissionId
      );

      if (isFormSubmissionIdMatch) {
        updatedCandidateWorkExperience = candidateWorkExperience.map((item) =>
          item.id === candidate.id ? candidate : item
        );
      } else {
        updatedCandidateWorkExperience = [
          ...candidateWorkExperience,
          candidate,
        ];
      }

      const parsedWorkExperience = parseWorkExperience(
        updatedCandidateWorkExperience
      );
      setCandidateProjectData(parsedWorkExperience);
      dispatch(fetchCandidate(id));
    }
  }, [candidate]);

  // Handle Submit
  const handleSubmit = (values, projectId) => {
    const formId = candidateProjectData[0]?.formId;
    const newData = {
      ...values,
      entityId: id,
      entityType: CandidateEntityConstant.CANDIDATE_WORK_EXPERIENCE,
      formData: JSON.stringify(values),
      formId: parseInt(formId),
    };

    dispatch(
      putCandidate({
        entity: CandidateEntityConstant.CANDIDATE_WORK_EXPERIENCE,
        id: projectId.projectId,
        newData: newData,
        config: {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      })
    ).then(() => {
      toast.success("Project summary updated successfully");
    });

    setEditModeId(null);
  };

  // Toggle Edit Mode
  const toggleEditMode = (id) => {
    setEditModeId((prevId) => (prevId === id ? null : id));
  };

  return (
    <>
      <div className="d-flex flex-row gap-3 mb-3 align-items-center fw-semibold mb-2">
        <span
          className={`mdi ${
            isExpanded ? "mdi-chevron-up" : "mdi-chevron-down"
          } cursor-pointer`}
          onClick={() => setIsExpanded(!isExpanded)}
        ></span>
        <span>Glance of my Projects</span>
      </div>
      <div
        className={`transition-container ${
          isExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="d-flex flex-column gap-4 mb-2">
          {candidateProjectData && candidateProjectData.length > 0 ? (
            candidateProjectData?.map((projectData, index) => {
              const project = projectData?.submissionData;
              const isEditMode = editModeId === projectData.id;
              return (
                <>
                  <div
                    key={index}
                    className="row d-flex flex-row align-items-start"
                  >
                    <div className="col-4 mb-3">
                      <CandidateProjectMedia
                        id={projectData?.id}
                        publicView={publicView}
                      />
                    </div>
                    <div className={`${publicView ? "col-8" : "col-7"} ps-0`}>
                      <div className="d-flex flex-column gap-1">
                        <span
                          className="h4"
                          style={{ color: "#3256ED", fontWeight: "lighter" }}
                        >
                          {project?.companyName} - {project?.title} Project
                        </span>
                        <p style={{ textAlign: "justify" }}>
                          {project?.Projectsnippet ? (
                            project?.Projectsnippet
                          ) : (
                            <span className="text-muted">
                              Details of project are not available.
                            </span>
                          )}
                        </p>
                      </div>
                      <div
                        className={`transition-container ${
                          isEditMode ? "expanded" : "collapsed"
                        }`}
                      >
                        <Formik
                          initialValues={project}
                          enableReinitialize={true}
                        >
                          {({ values, resetForm }) => (
                            <Form>
                              <div>
                                <span className="text-muted">
                                  Project Summary
                                </span>
                                <Field
                                  name="Projectsnippet"
                                  as="textarea"
                                  rows="5"
                                  placeholder="Enter Project Summary"
                                  className="form-control"
                                  style={{ border: "1px solid #E7E9EB" }}
                                />
                              </div>
                              <div className="d-flex flex-row justify-content-start gap-3 mt-3">
                                <div
                                  className="cursor-pointer"
                                  style={{ color: "#10B967" }}
                                  onClick={() =>
                                    handleSubmit(values, {
                                      projectId: projectData?.id,
                                    })
                                  }
                                >
                                  <span className="mdi mdi-check me-2"></span>
                                  <span>Save</span>
                                </div>
                                <div
                                  className="cursor-pointer"
                                  style={{ color: "#C71010" }}
                                  onClick={() => {
                                    resetForm();
                                    setEditModeId(null);
                                  }}
                                >
                                  <span className="mdi mdi-close me-2"></span>
                                  <span>Cancel</span>
                                </div>
                              </div>
                            </Form>
                          )}
                        </Formik>
                      </div>
                    </div>
                    <div
                      className={`${
                        publicView ? "" : "col-1"
                      }" d-flex justify-content-end align-items-start gap-3"`}
                    >
                      <div
                        className="rounded-circle cursor-pointer d-flex align-items-center justify-content-center fw-semibold"
                        style={{
                          backgroundColor: "#E7E9EB",
                          color: "#000",
                          width: "22px",
                          height: "22px",
                          visibility: publicView ? "hidden" : "visible",
                        }}
                        onClick={() => toggleEditMode(projectData.id)}
                      >
                        <span className="mdi mdi-pencil"></span>
                      </div>
                    </div>
                  </div>
                </>
              );
            })
          ) : (
            <span className="text-muted">No projects found.</span>
          )}
        </div>
      </div>

      <hr />
    </>
  );
};
